import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { BankRecord, FinanceApiService } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-banks',
  imports: [AinPageComponent, TableModule, FormsModule, ButtonModule, InputTextModule, DialogModule],
  template: `
    <ain-page [title]="t('finance.banks.title')" [description]="t('finance.banks.description')">
      <section class="card list-card">
        <div class="toolbar">
          <input pInputText type="text" [(ngModel)]="searchBankName" [placeholder]="t('finance.banks.searchBankName')" />
          <input pInputText type="text" [(ngModel)]="searchAccountNumber" [placeholder]="t('finance.banks.searchAccountNumber')" />
          <button pButton type="button" [label]="t('finance.banks.search')" (click)="search()"></button>
          <button pButton type="button" [label]="t('finance.banks.refresh')" severity="secondary" (click)="load()"></button>
        </div>
        <div class="toolbar">
          <button pButton type="button" [label]="t('finance.banks.addNew')" (click)="openCreate()"></button>
          <button pButton type="button" [label]="t('finance.banks.editSelected')" severity="secondary" [disabled]="!selected()" (click)="openEditSelected()"></button>
          <button pButton type="button" [label]="t('common.delete')" severity="danger" [disabled]="!selected()" (click)="deleteSelected()"></button>
        </div>

        <p-table [value]="rows()" selectionMode="single" [selection]="selected()" (selectionChange)="onSelect($event)" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('finance.banks.fields.bankName') }}</th>
              <th>{{ t('finance.banks.fields.branch') }}</th>
              <th>{{ t('finance.banks.fields.accountType') }}</th>
              <th>{{ t('finance.banks.fields.iban') }}</th>
              <th>{{ t('finance.banks.fields.accountNumber') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.bankName }}</button></td>
              <td>{{ row.branch || '-' }}</td>
              <td>{{ row.accountType || '-' }}</td>
              <td>{{ row.iban || '-' }}</td>
              <td>{{ row.accountNumber || '-' }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
        @if (successMessage()) {
          <p class="message success">{{ successMessage() }}</p>
        }
      </section>

      <p-dialog
        [header]="t('common.delete')"
        [visible]="deleteDialogOpen()"
        [modal]="true"
        [style]="{ width: '28rem', maxWidth: '95vw' }"
        (visibleChange)="onDeleteDialogVisibleChange($event)">
        <p>{{ t('finance.banks.messages.deleteConfirm') }}</p>
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDeleteDialog()"></button>
          <button pButton type="button" severity="danger" [label]="t('common.delete')" (click)="confirmDelete()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .message.success { color: #15803d; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
  `]
})
export class FinanceBanksComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  readonly rows = signal<BankRecord[]>([]);
  readonly selected = signal<BankRecord | null>(null);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly deleteDialogOpen = signal(false);
  searchBankName = '';
  searchAccountNumber = '';

  ngOnInit(): void {
    this.load();
  }

  load(selectedId?: string): void {
    this.errorMessage.set('');
    this.api.listAccountingBanks(this.searchBankName, this.searchAccountNumber).subscribe({
      next: data => {
        this.rows.set(data);
        const nextSelected = selectedId ? data.find(item => item.id === selectedId) ?? data[0] ?? null : data[0] ?? null;
        this.selected.set(nextSelected);
      },
      error: () => {
        this.rows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('finance.banks.messages.loadFailed'));
      }
    });
  }

  onSelect(row: BankRecord | null): void {
    this.selected.set(row);
  }

  search(): void {
    this.load();
  }

  openCreate(): void {
    void this.router.navigate(['/financial/payments/banks/new']);
  }

  openEditSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  deleteSelected(): void {
    if (!this.selected()) return;
    this.deleteDialogOpen.set(true);
  }

  onDeleteDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeDeleteDialog();
  }

  closeDeleteDialog(): void {
    this.deleteDialogOpen.set(false);
  }

  confirmDelete(): void {
    const row = this.selected();
    if (!row) return;

    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.deleteAccountingBank(row.id).subscribe({
      next: () => {
        this.closeDeleteDialog();
        this.successMessage.set(this.t('finance.banks.messages.deleted'));
        this.load();
      },
      error: () => {
        this.errorMessage.set(this.t('finance.banks.messages.deleteFailed'));
      }
    });
  }

  openDetail(id: string): void {
    void this.router.navigate(['/financial/payments/banks', id]);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
